import { TriangleDownIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  type Table as ReactTable,
  type Row,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import Loader from "@/shared/ui/Loader";

type ContextBlockProps<TData> = {
  row: TData;
  close: () => void;
};

type Props<TData> = {
  table: ReactTable<TData>;
  renderContextBlock?: (props: ContextBlockProps<TData>) => React.ReactNode;
  onRowClick?: (row: TData) => void;
  setSelectedRowInfo?: (row: TData) => void;
  defaultRowEstimate?: number;
  menuWidth?: number;
  scrollbarPaddingBottom?: string;
  scrollbarPaddingRight?: string;
  isLoading?: boolean;
};

export function TableVirtualize<TData>({
  table,
  renderContextBlock,
  onRowClick,
  setSelectedRowInfo,
  defaultRowEstimate = 46,
  menuWidth = 178,
  scrollbarPaddingBottom,
  scrollbarPaddingRight,
  isLoading,
}: Props<TData>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);

  const [contextData, setContextData] = useState<{
    row: TData;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const { rows } = table.getRowModel();

  const headerGroups = table.getHeaderGroups();
  const leafHeaderGroup = headerGroups[headerGroups.length - 1];
  const leafHeaders = leafHeaderGroup?.headers ?? [];
  const leafCount = leafHeaders.length;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => defaultRowEstimate + 10,
    measureElement: (el) => (el as HTMLElement).getBoundingClientRect().height + 10,
    overscan: 8,
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length
    ? totalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  const handleContextMenu = (e: React.MouseEvent, row: Row<TData>) => {
    if (!renderContextBlock) return;

    e.preventDefault();
    if (!scrollRef.current) return;

    const rect = scrollRef.current.getBoundingClientRect();
    const xInView = e.clientX - rect.left;
    const clampedX = rect.width - xInView < menuWidth ? Math.max(0, xInView - menuWidth) : xInView;

    const rowEl = e.currentTarget as HTMLElement;
    const rowRect = rowEl.getBoundingClientRect();
    const rowTopInView = rowRect.top - rect.top;

    setContextData({
      row: row.original,
      x: clampedX,
      y: rowTopInView,
      width: rect.width,
      height: rect.height,
    });
    if (setSelectedRowInfo) setSelectedRowInfo(row.original);
  };

  const handleRowClick = (e: React.MouseEvent, row: Row<TData>) => {
    if (contextData) {
      e.stopPropagation();
      e.preventDefault();
      setContextData(null);
    }
    onRowClick?.(row.original);
  };

  useEffect(() => {
    if (!contextData) return () => {};
    const handleClickOutside = (e: MouseEvent) => {
      if (blockRef.current && !blockRef.current.contains(e.target as Node)) {
        setContextData(null);
        if (setSelectedRowInfo) setSelectedRowInfo(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [contextData, setSelectedRowInfo]);

  useLayoutEffect(() => {
    if (!contextData || !blockRef.current || !scrollRef.current) return;

    const viewportRect = scrollRef.current.getBoundingClientRect();
    const blockRect = blockRef.current.getBoundingClientRect();

    const visibleTop = 0;
    const visibleBottom = viewportRect.height;

    const maxX = Math.max(0, viewportRect.width - blockRect.width);
    const maxY = Math.max(visibleTop, visibleBottom - blockRect.height);

    const nextX = Math.min(Math.max(contextData.x, 0), maxX);
    const nextY = Math.min(Math.max(contextData.y, visibleTop), maxY);

    if (nextX !== contextData.x || nextY !== contextData.y) {
      setContextData({ ...contextData, x: nextX, y: nextY });
    }
  }, [contextData]);

  const paddingBottomClass = scrollbarPaddingBottom ?? "";
  const paddingRightClass = scrollbarPaddingRight ?? "";

  const getColStyle = <TValue,>(
    colDef: ColumnDef<TData, TValue>,
  ): React.CSSProperties | undefined => {
    const own = (colDef as { style?: React.CSSProperties }).style;
    if (own) return own;

    const meta = colDef.meta as { style?: React.CSSProperties } | undefined;
    return meta?.style;
  };

  return (
    <div className="relative h-full">
      {isLoading && <Loader className="mr-2" />}
      <div
        ref={scrollRef}
        className={cn(
          "h-full w-full max-w-full overflow-y-auto overflow-x-hidden",
          "[--scrollbar-track-bg:#181818] [--scrollbar-thumb-bg:#434242] [scrollbar-gutter:stable]",
          paddingBottomClass,
          paddingRightClass,
        )}
      >
        <table
          ref={tableRef}
          className="w-full max-w-full table-auto border-collapse"
          style={{ tableLayout: "auto" }}
        >
          <thead className="sticky top-0 z-10 bg-dark-900">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="h-[46px]">
                {headerGroup.headers.map((header) => {
                  const colStyle = getColStyle(header.column.columnDef);
                  const meta = header.column.columnDef.meta as { isSort?: boolean } | undefined;
                  const isSortable = !!meta?.isSort;
                  const sorted = header.column.getIsSorted();

                  const handleHeaderClick = () => {
                    if (!isSortable) return;
                    if (sorted === "asc") header.column.toggleSorting(true);
                    else if (sorted === "desc") header.column.clearSorting();
                    else header.column.toggleSorting(false);
                  };

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ ...(colStyle ?? {}), height: "46px" }}
                      className={cn(
                        "group min-w-0 px-1 first:px-4 last:px-4 text-left text-[0.8125rem] font-medium transition-colors duration-200 h-[46px]",
                        isSortable && "cursor-pointer select-none",
                        sorted ? "text-accent-solid" : "text-white/55",
                        !sorted && isSortable && "hover:text-white",
                      )}
                      onClick={handleHeaderClick}
                    >
                      <div className="flex items-center h-[46px]">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {isSortable && (
                          <TriangleDownIcon
                            className={clsx(
                              "w-4 ml-1 transform transition-transform duration-300",
                              sorted && "text-accent-solid transition-colors duration-200",
                              sorted === "desc" && "rotate-180",
                            )}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden>
                <td colSpan={leafCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
              </tr>
            )}

            {virtualItems.map((item) => {
              const row = rows[item.index];
              return (
                <tr
                  key={row.id ?? item.index}
                  ref={rowVirtualizer.measureElement}
                  data-index={item.index}
                  onClick={(e) => handleRowClick(e, row)}
                  onContextMenu={(e) => handleContextMenu(e, row)}
                  className={clsx(
                    "bg-dark-800 cursor-pointer transition-colors duration-100 hover:bg-dark-700",
                    "[&:has([data-off-hover]:hover)]:hover:bg-dark-800",
                    contextData && "pointer-events-none",
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const colStyle = getColStyle(cell.column.columnDef);
                    return (
                      <td
                        key={cell.id}
                        className="relative min-w-0 pt-[0.9375rem] pb-[1.5625rem] px-1 first:px-4 last:px-4 text-14 align-middle first:rounded-l-xl last:rounded-r-xl"
                        style={colStyle}
                      >
                        <button
                          type="button"
                          className="absolute bottom-0 left-0 w-full h-2.5 bg-dark-900 pointer-events-auto"
                          data-off-hover
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onContextMenu={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                        />
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {paddingBottom > 0 && (
              <tr aria-hidden>
                <td colSpan={leafCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {contextData && renderContextBlock && (
        <div
          ref={blockRef}
          className="absolute z-10"
          style={{ left: contextData.x, top: contextData.y }}
        >
          {renderContextBlock({
            row: contextData.row,
            close: () => setContextData(null),
          })}
        </div>
      )}
    </div>
  );
}
